import { trackLoadboardEvent } from 'loadboard/core/LoadboardAnalytics';
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { LoadOfferSidebarIcon } from 'shared/icons/LoadOfferSidebarIcon';
import { LoadsSidebarIcon } from 'shared/icons/LoadsSidebarIcon';
import { LoadSuggestionsSidebarIcon } from 'shared/icons/LoadSuggestionsSidebarIcon';
import { SLBSidebarIcon } from 'shared/icons/SLBSidebarIcon';
import { useDispatcherProfile } from 'shared/modules/dispatcher/DispatcherAPI';
import { DispatcherDTO } from 'shared/modules/dispatcher/DispatcherDTO';
import { useLoadOffer } from 'shared/modules/load-offers/loadOfferCount';
import { useLoadSuggestionsUnseenCount } from 'shared/modules/load-suggestions/data/LoadSuggestionsAPI';
import { useShowAddBillingAddressDialog } from 'shared/modules/subscription/core/useShowAddBillingAddressDialog';
import { useCarrierSettings } from 'shared/settings/CarrierSettingsAPI';
import { useDispatcherProfileVisitedValue } from './IsDispatcherProfileVisited';
import { useLoadboardNudgeIndicator } from './useLoadboardNudgeIndicator';
import { useDispatcherPasswordEnhancement } from 'shared/modules/dispatcher/DispatcherPasswordEnhancement';
import { getLoadSuggestionsPageUrl } from 'shared/modules/load-suggestions/getLoadSuggestionsPageUrl';
import { useHasPendingOnboardingPackets } from 'shared/modules/onboarding-packets/data/OnboardingPacketsAPI';
import { useSubscriptionPaymentSeverity } from 'shared/layout/notification-stack/data/NotificationAPI';

export function useBottomItems() {
  const { data: settings } = useCarrierSettings();
  const { data: dispatcher } = useDispatcherProfile();
  const isEnteredDispatcherProfile = useDispatcherProfileVisitedValue();
  const shouldShowPasswordReminder = useDispatcherPasswordEnhancement(
    dispatcher as DispatcherDTO,
  );
  const loadOffersCount = useLoadOffer();
  const { doesBillingAddressNotExist } = useShowAddBillingAddressDialog();
  const hasPendingOnboardingPackets = useHasPendingOnboardingPackets();
  const subscriptionSeverity = useSubscriptionPaymentSeverity();

  const { data: loadSuggestionCount } = useLoadSuggestionsUnseenCount();

  const location = useLocation();
  const isSuperPayBankError = !!settings?.superpay.payment_error;

  const { isIndicatorVisible } = useLoadboardNudgeIndicator();

  const loadSuggestionsPageUrl = getLoadSuggestionsPageUrl();

  const bottomItems = useMemo(() => {
    return [
      {
        label: 'Loads',
        value: '/tms/loads',
        icon: <LoadsSidebarIcon />,
        active: location.pathname.includes('tms/loads'),
      },
      {
        value: 'load-offers',
        label: 'Offers',
        icon: <LoadOfferSidebarIcon />,
        hasBadge: Boolean(loadOffersCount),
        badgeContent: loadOffersCount,
      },
      {
        value: loadSuggestionsPageUrl,
        label: 'Suggested',
        icon: <LoadSuggestionsSidebarIcon />,
        hasBadge: Boolean(loadSuggestionCount?.number_of_unseen_suggestions),
        badgeContent: loadSuggestionCount?.number_of_unseen_suggestions,
        onClick: () => {
          trackLoadboardEvent({
            name: 'Driver Opened Suggested Loads',
            utm_medium: 'Suggested Tab Bar',
          });
        },
      },
      {
        value: '/loadboard',
        label: 'Loadboard',
        icon: <SLBSidebarIcon />,
        active: location.pathname.includes('loadboard'),
        hasBadge: isIndicatorVisible,
        badgeContent: undefined,
      },
    ];
  }, [
    location,
    loadOffersCount,
    loadSuggestionCount,
    isIndicatorVisible,
    loadSuggestionsPageUrl,
  ]);

  const hasMenuBadge =
    !doesBillingAddressNotExist &&
    !isSuperPayBankError &&
    !hasPendingOnboardingPackets &&
    !subscriptionSeverity &&
    (!!isEnteredDispatcherProfile || !shouldShowPasswordReminder);

  return { bottomItems, hasMenuBadge };
}
